import React, { useMemo } from "react";

import { useNavigate } from "react-router";

import { ComponentData } from "@companion-core/shared/app/Interfaces/API";
import { PostListLayoutProps } from "@companion-core/shared/app/Interfaces/blocks";
import { getCloudinaryOptimizedUrl } from "@companion-core/shared/app/Utils/cloudinary";
import { getSanitizedDescriptionAlt } from "@companion-core/shared/app/Utils/componentHelper";
import { useDynamicPageStore } from "@companion-core/shared/app/Store/dynamicPageStore";
import { sanitizeHtml } from "@companion-core/shared/app/Utils/htmlSanitizer";
import { Button } from "@companion-core/shared/app/Components/Button";
import { redirectTo } from "@companion-core/web/src/Utilities/navigation";
import { useMediaQuery } from "@companion-core/web/src/Utilities/mediaQuery";

import "@companion-core/web/src/Assets/Styles/scss/postListAlternateLayout.scss";

type ListElementProps = {
  component: ComponentData;
  key: string;
  index: number;
};

export const PostListAlternateLayout = ({ cards, blockSlug = "" }: PostListLayoutProps) => {
  const { blocksBySlug } = useDynamicPageStore();
  const navigate = useNavigate();
  const isMobileSize = useMediaQuery("(max-width: 768px)");
  const colorIndex = useMemo(() => {
    return blocksBySlug[blockSlug]?.isLoaded ? (blocksBySlug[blockSlug].colorIndex ?? 0) : 0;
  }, [blocksBySlug, blockSlug]);

  const ListElement = ({ component, index }: ListElementProps) => {
    return (
      <div
        className={`post-list-alternate-layout__wrapper ${(colorIndex + index) % 2 === 0 ? "colored-background--odd" : "colored-background--even"}`}>
        <div className="post-list-alternate-layout__content">
          {component.medias && component.medias.length > 0 && (
            <img
              className="post-list-alternate-layout__media"
              data-testid={`post-list-alternate__media--${component.id}`}
              src={getCloudinaryOptimizedUrl(component.medias[0].url, {
                contentType: "image",
                width: window.innerWidth,
              })}
              alt={getSanitizedDescriptionAlt(component.medias[0].description_alt) ?? ""}
            />
          )}
          <div className="post-list-alternate-layout__text-content">
            {component.title && (
              <span
                className="post-list-alternate-layout__title super-title-3"
                data-testid={`post-list-alternate__title--${component.id}`}>
                {component.title}
              </span>
            )}
            {component.subtitle && (
              <span
                className="post-list-alternate-layout__subtitle title-8"
                data-testid={`post-list-alternate__subtitle--${component.id}`}>
                {component.subtitle}
              </span>
            )}
            {component.description && (
              <span
                className="post-list-alternate-layout__description body-text-4"
                data-testid={`post-list-alternate__description--${component.id}`}
                // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(component.description) }}
              />
            )}
            {component.button_link && component.button_title && (
              <Button
                testID="post-list-alternate__cta"
                label={component.button_title}
                classNames={["post-list-alternate-layout__CTA"]}
                size={isMobileSize ? "full" : "large"}
                onClick={() =>
                  redirectTo(
                    component.button_link ?? "/",
                    component?.button_type ?? "internal",
                    navigate
                  )
                }
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    cards && (
      <div className="post-list-alternate-layout" data-testid="post-list-alternate">
        {cards.map((card, index) => (
          <ListElement
            key={`post-list-alternate-layout__element--${card.id}`}
            component={card}
            index={index}
          />
        ))}
      </div>
    )
  );
};
